import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, ChevronRight, Plus, Wallet, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useMe } from "@/shared/api/hooks";
import { useDraftStore } from "@/shared/store/draft";
import { useRoleStore } from "@/shared/store/role";
import { useBloggerPayouts } from "@/shared/api/wb";
import { fmtRubFull } from "@/shared/lib/format";
import { TopUpModal } from "@/pages/TopUpModal";
import { RoleSwitcher } from "./RoleSwitcher";

const NOTIFS = 3;

type TopbarProps = {
  sidebarHidden: boolean;
  onToggleSidebar: () => void;
};

export function Topbar({ sidebarHidden, onToggleSidebar }: TopbarProps) {
  const { data: me } = useMe();
  const draftProductName = useDraftStore((s) => s.draft.productName);
  const { pathname } = useLocation();
  const crumbs = breadcrumbsFor(pathname, draftProductName);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const role = useRoleStore((s) => s.role);
  const activeBloggerId = useRoleStore((s) => s.activeBloggerId);
  const { data: bloggerPayouts } = useBloggerPayouts(role === "blogger" ? activeBloggerId : null);

  return (
    <header className="h-[60px] flex items-center gap-4 px-4 border-b border-ink-12 bg-[var(--bg)] sticky top-0 z-30">
      <button
        onClick={onToggleSidebar}
        title={sidebarHidden ? "Показать меню" : "Скрыть меню"}
        className="w-8 h-8 grid place-items-center rounded-md text-ink-40 hover:bg-ink-6 hover:text-ink flex-shrink-0 transition-colors"
      >
        {sidebarHidden ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
      </button>

      <nav className="flex items-center gap-2 text-[13px] text-ink-40 min-w-0 flex-1">
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-2 min-w-0">
            {i > 0 && <ChevronRight size={12} className="flex-shrink-0" />}
            {c.to ? (
              <Link to={c.to} className="hover:text-ink truncate">{c.label}</Link>
            ) : (
              <span className="text-ink font-semibold truncate">{c.label}</span>
            )}
          </span>
        ))}
      </nav>

      <RoleSwitcher />

      {role === "blogger" ? (
        <div className="flex items-stretch h-9 rounded-md bg-white border border-ink-12 overflow-hidden">
          <Link to="/blogger-cabinet/balance" className="flex items-center gap-2.5 px-3.5 hover:bg-ink-6 transition-colors" title="Баланс и выплаты">
            <Wallet size={14} className="text-ink-60" />
            <span className="font-mono font-semibold text-[13px] tabular-nums">{fmtRubFull(bloggerPayouts?.balance_rub ?? 0)}</span>
          </Link>
        </div>
      ) : (
        <>
          <button
            title="Уведомления"
            className="w-9 h-9 grid place-items-center rounded-md text-ink-60 hover:bg-ink-6 hover:text-ink relative"
          >
            <Bell size={16} />
            {NOTIFS > 0 && (
              <span
                className="absolute top-1 right-1 w-3.5 h-3.5 grid place-items-center rounded-full bg-pink text-white text-[9px] font-bold"
                style={{ border: "2px solid var(--bg)" }}
              >{NOTIFS}</span>
            )}
          </button>

          <div className="flex items-stretch h-9 rounded-md bg-white border border-ink-12 overflow-hidden">
            <Link to="/billing" className="flex items-center gap-2.5 px-3.5 hover:bg-ink-6 transition-colors" title="История пополнений">
              <Wallet size={14} className="text-ink-60" />
              <span className="font-mono font-semibold text-[13px] tabular-nums">{fmtRubFull(me?.balance_rub ?? 0)}</span>
            </Link>
            <button
              title="Пополнить баланс"
              onClick={() => setTopUpOpen(true)}
              className="inline-flex items-center gap-1 px-3 bg-lime text-[var(--fg-on-accent)] text-[12.5px] font-display font-bold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] transition-colors"
            >
              <Plus size={13} strokeWidth={3} /> Пополнить
            </button>
          </div>
          <TopUpModal open={topUpOpen} onClose={() => setTopUpOpen(false)} />
        </>
      )}

      {me && (
        <div className="flex items-center gap-2.5 pl-2 pr-3 h-9 rounded-md bg-white border border-ink-12">
          <div
            className="w-7 h-7 rounded-full grid place-items-center font-display font-bold text-white text-[11px] flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #C2A6FF, #540D6E)" }}
          >
            {initialsOf(me.name)}
          </div>
          <div className="text-[12px] leading-snug min-w-0 hidden md:block">
            <div className="font-semibold truncate max-w-[160px]">{me.name}</div>
            <div className="text-ink-40 truncate max-w-[160px]">
              {role === "blogger" ? "Смотрим как блогер" : me.is_admin ? "Админ · WOWBLOGGER" : "ООО «Бренд»"}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function initialsOf(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
}

function breadcrumbsFor(pathname: string, draftName: string): { label: string; to?: string }[] {
  if (pathname.startsWith("/campaign/new")) {
    return [
      { label: "Мои РК", to: "/account/projects" },
      { label: draftName || "Новая кампания" },
    ];
  }
  if (pathname.match(/^\/campaign\/[^/]+\/chats/)) {
    return [
      { label: "Мои РК", to: "/account/projects" },
      { label: "Чаты" },
    ];
  }
  if (pathname.match(/^\/campaign\/[^/]+$/)) {
    return [
      { label: "Мои РК", to: "/account/projects" },
      { label: "WOWBLOGGER" },
    ];
  }
  if (pathname === "/inbox") return [{ label: "Лента чатов" }];
  if (pathname === "/billing") return [{ label: "Финансы" }];
  if (pathname === "/blogger-cabinet/inbox") return [{ label: "Входящие заявки" }];
  if (pathname === "/blogger-cabinet/balance") return [{ label: "Баланс и выплаты" }];
  if (pathname === "/analytics") return [{ label: "Аналитика" }];
  if (pathname === "/docs") return [{ label: "Документы · ОРД" }];
  if (pathname === "/account/projects") return [{ label: "Мои РК" }];
  return [{ label: "Кабинет" }];
}
